import type { FC } from 'react';
import type { IEvaultApi } from './use-evault-api';
import type { IVaultDocument } from './types';

import { useCallback, useEffect, useRef, useState } from 'react';

import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { useFaceVerification, useNotification } from 'hooks';
import { KV, Modal } from 'views/design-system';

import { FaceVerify } from './face-verify';
import type { IFaceCaptureOutcome } from './face-verify';
import { formatDisplayId } from './derive';
import { CHECKOUT_REASON_MAX, useCheckout } from './use-checkout';

/** `FACE_NOT_FOUND` / `FACE_SERVICE_UNAVAILABLE` → the copy the panel shows; any other
    code falls through to the server's own message. Same table the transfer modal uses. */
const FACE_ERROR_COPY: Partial<Record<string, string>> = {
	FACE_NOT_FOUND:
		'We could not see a face. Face the camera in good light and try again.',
	FACE_SERVICE_UNAVAILABLE:
		'Face verification is unavailable right now. Try again in a few minutes.',
};

export interface ICheckoutModalProps {
	api: IEvaultApi;
	/** The ONE document leaving the vault. */
	document: IVaultDocument;
	onClose: () => void;
	/** Committed and acknowledged: clear the selection and refetch. */
	onCheckedOut: () => void;
	/** The list is stale (409) but the modal is still up. */
	onRefresh: () => void;
	/** 403 `EVAULT_NOT_ENTITLED` — close and open the add-on instead. */
	onUnentitled: () => void;
}

/**
 * Check an original out of the vault (SIM-23 §B5) — four steps in ONE modal.
 *
 * Deliberately the transfer modal's shape, so a reader recognises where they are:
 * intent → face → confirm → done, mounted only while open so its state starts fresh on
 * every attempt.
 *
 * ONE DOCUMENT, NEVER A SELECTION. A transfer moves a set because the set moves
 * together; a checkout hands one sealed file and one certificate to one person, and a
 * bulk version would have to explain what happens when three of five succeed.
 *
 * WHAT THE LAST STEP CLAIMS is only what the commit response asserted. The certificate
 * button appears when the server returned a certificate and a sentence appears instead
 * when it did not, because issuance is allowed to fail after custody has already ended
 * — a button that downloads nothing would be the worse half of that trade.
 */
export const CheckoutModal: FC<ICheckoutModalProps> = ({
	api,
	document: doc,
	onClose,
	onCheckedOut,
	onRefresh,
	onUnentitled,
}) => {
	const { successNotification, errorNotification } = useNotification();
	const {
		step,
		reason,
		setReason,
		busy,
		error,
		conflicted,
		initiated,
		committed,
		initiate,
		verified,
		back,
		commit,
		cancelPending,
	} = useCheckout({ api, document: doc, onUnentitled, onRefresh });
	const { verify: verifyFace, busy: faceBusy } = useFaceVerification();

	/* The one caller of `useFaceVerification` for this flow — it owns the pending
	   checkout's id, so it is the one that can turn a capture into a call. The
	   checkout id is a 24-hex ObjectId, which is all the mint validates, so it travels
	   in the `transferId` field unchanged (commented on both server sides). */
	const onCaptured = useCallback(
		async (images: string[]): Promise<IFaceCaptureOutcome> => {
			if (!initiated) {
				return { ok: false, message: 'This checkout is no longer pending.' };
			}
			const result = await verifyFace(initiated._id, images);
			if (result.ok) return { ok: true, token: result.token };
			return {
				ok: false,
				message: FACE_ERROR_COPY[result.code] ?? result.message,
			};
		},
		[initiated, verifyFace]
	);

	/* One toast per commit, fired where the commit lands rather than where the modal is
	   dismissed — the original has left at that moment whether or not anyone presses
	   Done. */
	const toasted = useRef(false);
	useEffect(() => {
		if (!committed || toasted.current) return;
		toasted.current = true;
		successNotification(
			`${formatDisplayId(committed.document.displayId)} checked out`
		);
	}, [committed, successNotification]);

	const [downloading, setDownloading] = useState(false);

	/**
	 * Save the sealed original the checkout handed over.
	 *
	 * Fetched rather than opened in a tab, because `originalUrl` is an AUDITED route that
	 * needs the eVault bearer and a `window.open` carries no headers — the same reason
	 * and the same mechanics as the drawer's `Download original`. A refusal is surfaced
	 * verbatim: the server declines to serve a file whose bytes disagree with its seal,
	 * and that sentence is the finding.
	 */
	const downloadOriginal = useCallback(async () => {
		if (!committed?.originalUrl || downloading) return;
		setDownloading(true);
		try {
			const response = await fetch(`${API_HOST}${committed.originalUrl}`, {
				headers: { Authorization: `Bearer ${api.vaultToken}` },
			});
			if (!response.ok) {
				const body = await response.json().catch(() => null);
				errorNotification(
					body?.message ?? 'The original could not be downloaded.'
				);
				return;
			}
			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			const anchor = window.document.createElement('a');
			anchor.href = url;
			const name = committed.document.name;
			anchor.download = /\.pdf$/i.test(name) ? name : `${name}.pdf`;
			window.document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			/* Revoked on the next tick, not immediately: Chrome starts the save
			   asynchronously and a URL revoked in the same frame can cancel it. */
			setTimeout(() => URL.revokeObjectURL(url), 0);
		} catch {
			errorNotification('The original could not be downloaded.');
		} finally {
			setDownloading(false);
		}
	}, [api.vaultToken, committed, downloading, errorNotification]);

	/**
	 * ×, backdrop and Escape all land here.
	 *
	 * Leaving `verify` or `confirm` by any route cancels the pending record — the same
	 * call Back makes. Skipping it because the user pressed Escape rather than Back would
	 * leave a pending checkout to expire on its own, and the server refuses a second one
	 * while it lives, so the next attempt would be refused for fifteen minutes with no
	 * visible cause. Closing on `done` is Done: the original has already left.
	 */
	const close = useCallback(() => {
		if (busy || faceBusy || downloading) return;
		if (step === 'done') {
			onCheckedOut();
			return;
		}
		if ((step === 'verify' || step === 'confirm') && !conflicted) {
			void cancelPending().then(onClose);
			return;
		}
		onClose();
	}, [
		busy,
		cancelPending,
		conflicted,
		downloading,
		faceBusy,
		onCheckedOut,
		onClose,
		step,
	]);

	const title =
		step === 'confirm-intent'
			? 'Check out of the vault'
			: step === 'verify'
				? "Verify it's you"
				: step === 'confirm'
					? 'Confirm checkout'
					: 'Checked out of the vault';

	return (
		<Modal
			open
			title={title}
			size="wide"
			className="evault-modal"
			sub={
				step === 'confirm-intent'
					? 'The sealed original leaves the vault and stays with you.'
					: undefined
			}
			onClose={close}
			footer={
				step === 'confirm-intent' ? (
					<>
						<button className="dbtn" type="button" disabled={busy} onClick={close}>
							Cancel
						</button>
						<button
							className="dbtn pri"
							type="button"
							disabled={busy}
							onClick={() => void initiate()}
						>
							Continue
						</button>
					</>
				) : step === 'verify' ? (
					<button
						className="dbtn"
						type="button"
						disabled={faceBusy}
						onClick={() => void back()}
					>
						Back
					</button>
				) : step === 'confirm' ? (
					conflicted ? (
						<button className="dbtn pri" type="button" onClick={onClose}>
							Close
						</button>
					) : (
						<>
							<button
								className="dbtn"
								type="button"
								disabled={busy}
								onClick={() => void back()}
							>
								Back
							</button>
							<button
								className="dbtn pri"
								type="button"
								disabled={busy}
								onClick={() => void commit()}
							>
								Check out
							</button>
						</>
					)
				) : (
					<button className="dbtn pri" type="button" onClick={onCheckedOut}>
						Done
					</button>
				)
			}
		>
			{step === 'confirm-intent' && (
				<>
					<div className="tchips">
						<span className="vch strt" title={doc.name}>
							{formatDisplayId(doc.displayId)} {doc.name}
						</span>
					</div>

					<div className="tlab">Reason</div>
					<div className="thelp">
						Optional. Recorded on the audit trail and printed on the certificate of
						custody.
					</div>
					<textarea
						className="rsn"
						value={reason}
						maxLength={CHECKOUT_REASON_MAX}
						disabled={busy}
						rows={3}
						placeholder="e.g. produced to counsel for the Harding matter"
						onChange={e => setReason(e.target.value)}
					/>

					<div className="note warn">
						This removes the original from the vault permanently. You will receive
						the sealed original and a certificate of custody recording this
						checkout. It can only return to the vault as a new re-vaulted
						iteration.
					</div>

					{/* The server's own sentence, under the screen that caused it — a legal
					    hold, an original already checked out, one that is not yours. */}
					{!!error && <div className="merr">{error}</div>}
				</>
			)}

			{step === 'verify' && (
				<FaceVerify onCaptured={onCaptured} onContinue={verified} busy={faceBusy} />
			)}

			{step === 'confirm' && (
				<>
					{/* The SERVER's summary, not a copy this page kept — the whole reason
					    initiate and commit are two calls. */}
					<KV
						rows={[
							[
								'Document',
								`${formatDisplayId(initiated?.document.displayId ?? '')} ${
									initiated?.document.name ?? ''
								}`,
							],
							['Reason', initiated?.reason ?? '—'],
						]}
					/>
					<div className="note">This confirmation expires in 15 minutes.</div>
					{!!error && <div className="merr">{error}</div>}
				</>
			)}

			{step === 'done' && committed && (
				<>
					<div className="tdone">
						{formatDisplayId(committed.document.displayId)} has been checked out of
						the vault.
					</div>
					<ul className="dones">
						<li>The checkout has been recorded in the audit trail.</li>
					</ul>
					<div className="dbtns">
						{/* Only when the server said the stored original is addressable.
						    `originalUrl` is '' when it is not, and a button that downloads
						    nothing is worse than its absence — the drawer's own Download
						    original is the place to retry. */}
						{!!committed.originalUrl && (
							<button
								className="dbtn"
								type="button"
								disabled={downloading}
								onClick={() => void downloadOriginal()}
							>
								{downloading ? 'Preparing…' : 'Download the original (PDF)'}
							</button>
						)}
						{committed.certificate?.downloadUrl && (
							<a
								className="dbtn"
								href={committed.certificate.downloadUrl}
								target="_blank"
								rel="noopener noreferrer"
							>
								Download certificate of custody (PDF)
							</a>
						)}
					</div>
					{!committed.certificate && (
						<div className="foot frm">
							The certificate could not be issued. Open the document and use
							Download certificate to retry.
						</div>
					)}
				</>
			)}
		</Modal>
	);
};

export default CheckoutModal;
